import { useRef, type ReactNode } from "react";
import { m as motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

interface Props {
  children: ReactNode;
  className?: string;
  glow?: "mint" | "butter" | "peach" | "none";
  intensity?: number;
}

const glowMap = {
  mint: "from-mint/25",
  butter: "from-butter/25",
  peach: "from-peach/25",
  none: "from-transparent",
} as const;

export function TiltCard({ children, className, glow = "mint", intensity = 6 }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 200, damping: 20 });
  const sy = useSpring(my, { stiffness: 200, damping: 20 });
  const reduce = useReducedMotion();
  const rotateX = useTransform(sy, [-0.5, 0.5], [intensity, -intensity]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-intensity, intensity]);
  const gx = useTransform(sx, [-0.5, 0.5], ["20%", "80%"]);
  const gy = useTransform(sy, [-0.5, 0.5], ["20%", "80%"]);

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ rotateX, rotateY, transformPerspective: 900 }}
      className={cn("group relative", className)}
    >
      {children}
      {glow !== "none" && (
        <motion.div
          aria-hidden="true"
          style={{
            background: `radial-gradient(circle at var(--gx) var(--gy), color-mix(in oklab, var(--mint) 28%, transparent), transparent 55%)`,
            // @ts-expect-error CSS vars
            "--gx": gx,
            "--gy": gy,
          }}
          className={cn(
            "pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-gradient-to-br",
            glowMap[glow],
          )}
        />
      )}
    </motion.div>
  );
}
